import { useCallback } from 'react';
import type { FormikProps } from 'formik';
import { useLazyGetTowProVinLookupQuery } from '@rsos/reduxDataStore/src/features/resourceRequests';
import { VIN_LENGTH, type TowProFormValues } from './towProFormConstants';

/**
 * Auto-populate Year, Make, and Model from the TowPro VIN lookup once a full
 * 17-character VIN is entered. Populated fields stay editable, and lookup
 * failures leave the fields for manual entry.
 */
export function useTowProVinAutofill(formRef: {
  readonly current: FormikProps<TowProFormValues> | null;
}): (vin: string) => Promise<void> {
  const [triggerVinLookup] = useLazyGetTowProVinLookupQuery();

  return useCallback(
    async (vin: string): Promise<void> => {
      const trimmedVin = vin.trim();
      if (trimmedVin.length !== VIN_LENGTH) {
        return;
      }

      try {
        const result = await triggerVinLookup(trimmedVin, true).unwrap();
        const match = result.Results.find(
          vehicle => vehicle.Make || vehicle.Model || vehicle.ModelYear,
        );
        const formik = formRef.current;
        if (!match || !formik) {
          return;
        }

        if (match.Make) {
          formik.setFieldValue('make', match.Make);
        }
        if (match.Model) {
          formik.setFieldValue('model', match.Model);
        }
        if (match.ModelYear) {
          formik.setFieldValue('vehicle_year', match.ModelYear);
        }
      } catch {
        // The VIN lookup is best-effort; the vehicle fields remain manual.
      }
    },
    [formRef, triggerVinLookup],
  );
}
